<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-indigo-200 rounded-lg shadow-lg border border-gray-700 overflow-hidden animate-scaleIn flex flex-col" style="max-height:90vh;">
      <!-- Header -->
      <div class="flex justify-between items-center p-4 border-b border-gray-700 bg-gray-800">
        <h2 class="text-lg font-semibold text-indigo-300">{{ map.name }}</h2>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group focus:outline-none"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>
      <!-- Tabs -->
      <div class="grid grid-cols-3 sm:grid-cols-5 border-b border-gray-700 bg-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
          :class="activeTab === tab.id
            ? 'text-indigo-300 border-b-2 border-indigo-600'
            : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-600 border border-transparent'"
        >
          {{ tab.name }}
          <span v-if="activeTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
        </button>
      </div>
      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
        <!-- Map Info Tab -->
        <div v-if="activeTab === 'mapInfo'" class="space-y-2 h-72 overflow-auto pb-20 animate-fadeIn" id="style-1">
          <!-- Basic Info -->
          <div class="bi-card">
            <div class="bi-header">
              <h3 class="bi-title">
                <i class="fas fa-info-circle"></i>
                ข้อมูลพื้นฐาน
              </h3>
            </div>
            <div class="bi-grid">
              <div class="bi-item"><i class="fas fa-user"></i><span>เลเวลขั้นต่ำ: {{ map.requiredLevel || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-bolt"></i><span>พลังงาน: {{ map.requiredEnergy || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-star"></i><span>ชื่อเสียง: {{ map.requiredRenown || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-frown"></i><span>ลดความสุข: {{ map.reduceHappy || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-skull-crossbones"></i><span>ซอมบี้: {{ map.minZombies || 0 }}-{{ map.maxZombies || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-running"></i><span>จำนวนครั้งที่หนีได้: {{ map.escapeAttempts || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-coins"></i><span>เหรียญสำหรับหนี: {{ map.escapeCoins || 0 }}</span></div>
              <div class="bi-item"><i class="fas fa-door-closed"></i><span>จำนวนห้องที่ต้องการ: {{ map.roomRequired || 1 }}</span></div>
              <div class="bi-item bi-span2"><i class="fas fa-map"></i><span>แผนที่ที่ต้องมีก่อน: {{ map.requiredMap || 'ไม่มี' }}</span></div>
            </div>
          </div>
          <!-- Entry Cost -->
          <div
            v-if="(map.requiredCoins || 0) + (map.requiredDiamonds || 0) + (map.requiredCash || 0) + (map.requiredCredit || 0) + (map.requiredGold || 0) >= 1"
            class="fee-card"
          >
            <div class="fee-header">
              <h3 class="fee-title">
                <i class="fas fa-door-open"></i>
                ค่าเข้า
              </h3>
            </div>
            <div class="fee-grid">
              <div v-if="map.requiredCoins >= 1" class="fee-chip">
                <i class="fas fa-coins text-gray-100"></i>
                <span>{{ map.requiredCoins }}</span>
              </div>
              <div v-if="map.requiredGold >= 1" class="fee-chip">
                <i class="fas fa-g fee-gold"></i>
                <span>{{ map.requiredGold }}</span>
              </div>
              <div v-if="map.requiredDiamonds >= 1" class="fee-chip">
                <i class="fas fa-gem fee-diamond"></i>
                <span>{{ map.requiredDiamonds }}</span>
              </div>
              <div v-if="map.requiredCash >= 1" class="fee-chip">
                <i class="fas fa-money-bill fee-cash"></i>
                <span>{{ map.requiredCash }}</span>
              </div>
              <div v-if="map.requiredCredit >= 1" class="fee-chip">
                <i class="fas fa-credit-card fee-credit"></i>
                <span>{{ map.requiredCredit }}</span>
              </div>
            </div>
          </div>
          <!-- Occupation System -->
          <div v-if="user" class="occ-container">
            <div class="occ-header">
              <h3 class="occ-title">
                <i class="fas fa-chess-rook"></i>
                ระบบยึดครองแผนที่
              </h3>
              <span
                class="occ-badge"
                :data-allowed="map.isOccupationAllowed ? 'true' : 'false'"
              >
                <i :class="map.isOccupationAllowed ? 'fas fa-check-circle' : 'fas fa-ban'"></i>
                {{ map.isOccupationAllowed ? 'ยึดครองได้' : 'ไม่รองรับการยึดครอง' }}
              </span>
            </div>
            <!-- Not allowed -->
            <div v-if="!map.isOccupationAllowed" class="occ-note">
              แผนที่นี้ยังไม่รองรับระบบยึดครอง
            </div>
            <!-- Allowed -->
            <div v-else class="occ-body">
              <!-- KPI row -->
              <div class="occ-kpi-grid">
                <div class="occ-kpi-card">
                  <div class="occ-kpi-icon"><i class="fas fa-chart-line"></i></div>
                  <div class="occ-kpi-body">
                    <p class="occ-kpi-label">คะแนนยึดครอง</p>
                    <p class="occ-kpi-value">{{ map.occupationScore || 0 }}</p>
                  </div>
                </div>
                <div class="occ-kpi-card">
                  <div class="occ-kpi-icon"><i class="fas fa-gem"></i></div>
                  <div class="occ-kpi-body">
                    <p class="occ-kpi-label">ค่าใช้จ่ายท้าประลอง</p>
                    <p class="occ-kpi-value">
                      {{ map.challengeCostDiamonds || 0 }}
                      <span class="occ-kpi-sub">เพชร</span>
                    </p>
                  </div>
                </div>
                <div class="occ-kpi-card">
                  <div class="occ-kpi-icon"><i class="fas fa-trophy"></i></div>
                  <div class="occ-kpi-body">
                    <p class="occ-kpi-label">รางวัลรายวันผู้ยึดครอง</p>
                    <p class="occ-kpi-value">
                      {{ map.dailyDiamondReward || 0 }}
                      <span class="occ-kpi-sub">เพชร</span>
                    </p>
                  </div>
                </div>
                <div class="occ-kpi-card">
                  <div class="occ-kpi-icon"><i class="fas fa-percentage"></i></div>
                  <div class="occ-kpi-body">
                    <p class="occ-kpi-label">โอกาสพบผู้ยึดครอง</p>
                    <p class="occ-kpi-value">{{ map.encounterChance || 0 }}%</p>
                    <div class="occ-progress">
                      <div class="occ-progress-bar" :style="{ width: Math.min(100, Math.max(0, map.encounterChance || 0)) + '%' }"></div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Current Occupier -->
              <div class="occ-section">
                <h4 class="occ-subtitle">ผู้ยึดครองปัจจุบัน</h4>
                <!-- Loading -->
                <div v-if="isLoadingOccupier" class="occ-skeleton">
                  <div class="occ-skel-avatar occ-shimmer"></div>
                  <div class="occ-skel-lines">
                    <span class="occ-skel-line occ-shimmer"></span>
                    <span class="occ-skel-line occ-shimmer"></span>
                    <span class="occ-skel-line occ-shimmer"></span>
                  </div>
                </div>
                <!-- Empty -->
                <div v-else-if="!map.occupierUid" class="occ-empty">
                  <div class="occ-empty-avatar"><i class="fas fa-user-slash"></i></div>
                  <div>
                    <p class="occ-empty-title">ยังไม่มีผู้ยึดครอง</p>
                    <p class="occ-empty-desc">แผนที่ว่างสำหรับการท้าประลอง</p>
                  </div>
                </div>
                <!-- Has occupier -->
                <div v-else class="occ-occupier">
                  <div class="occ-occupier-left">
                    <img
                      v-if="occupierDetails.imageUrl"
                      :src="occupierDetails.imageUrl"
                      alt="Occupier"
                      class="occ-avatar"
                      @error="handleImageError"
                    />
                    <div class="occ-occupier-info">
                      <p class="occ-occupier-name">{{ occupierDetails.characterName || 'N/A' }}</p>
                      <div class="occ-pills">
                        <span class="occ-pill"><i class="fas fa-star"></i> เลเวล {{ occupierDetails.level || 0 }}</span>
                        <span class="occ-pill"><i class="fas fa-door-closed"></i> ห้อง {{ occupierDetails.room || 0 }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="occ-occupier-hint">
                    <p>ผู้ยึดครองจะได้รับรางวัลรายวัน</p>
                    <p>และมีสิทธิ์ป้องกันตำแหน่ง</p>
                  </div>
                </div>
                <!-- ===== Enhanced Actions: Treasure & Resign ===== -->
                <div v-if="map.isOccupationAllowed" class="mt-4 space-y-4">
                  <!-- Treasure Box Action -->
                  <div class="relative">
                    <!-- Primary action row -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                      <div class="flex items-center gap-2">
                        <button
                          v-if="!isOpeningTreasure"
                          @click="confirmOpen = !map.treasureOpened ? true : false"
                          :disabled="map.treasureOpened || !isCurrentUserOccupier"
                          class="group inline-flex items-center gap-3 px-4 py-2 rounded-md transition-colors duration-200
                                 focus:outline-none border"
                          :class="[
                            map.treasureOpened || !isCurrentUserOccupier
                              ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed'
                              : 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500',
                          ]"
                          aria-label="เปิดกล่องสมบัติ"
                          @mouseenter="showHintOpen = true"
                          @mouseleave="showHintOpen = false"
                        >
                          <span class="relative inline-flex">
                            <i class="fas fa-treasure-chest text-lg"
                               :class="map.treasureOpened ? 'text-yellow-300 opacity-60' : 'text-yellow-300'"></i>
                          </span>
                          <div class="flex flex-col items-start leading-tight">
                            <span class="font-semibold text-sm">เปิดกล่องสมบัติ</span>
                            <span class="text-xxs opacity-80" v-if="!map.treasureOpened">รับรางวัลยึดครองประจำวัน</span>
                            <span class="text-xxs opacity-80" v-else>กล่องถูกเปิดแล้ว</span>
                          </div>
                        </button>
                        <!-- Loading state -->
                        <div
                          v-else
                          class="inline-flex items-center gap-3 px-4 py-2 rounded-md bg-indigo-600 text-white border border-indigo-500"
                        >
                          <i class="fas fa-treasure-chest animate-shake text-yellow-300 text-xl"></i>
                          <span class="animate-pulse text-sm">กำลังเปิดกล่องสมบัติ...</span>
                        </div>
                        <!-- Tiny hover hint -->
                        <div
                          v-if="showHintOpen && !map.treasureOpened && !isOpeningTreasure"
                          class="hidden sm:block text-xxs bg-gray-900 border border-gray-700 px-2 py-1 rounded-md"
                        >
                          ต้องเป็นผู้ยึดครองจึงจะเปิดได้
                        </div>
                      </div>
                      <!-- Right info side -->
                      <div class="text-xxs sm:text-xs text-indigo-300 space-y-1">
                        <p v-if="map.treasureOpened">{{ countdownText }}</p>
                        <p v-if="treasureMessage" :class="treasureMessage.includes('สำเร็จ') ? 'text-green-400' : 'text-red-400'">
                          {{ treasureMessage }}
                        </p>
                      </div>
                    </div>
                    <!-- Step 2: Confirm panel -->
                    <transition name="fade">
                      <div
                        v-if="confirmOpen && !map.treasureOpened"
                        class="mt-2 p-3 rounded-md border bg-gray-900 border-gray-700"
                      >
                        <div class="flex items-start gap-3">
                          <i class="fas fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
                          <div class="flex-1">
                            <p class="text-sm text-indigo-200 font-medium">ยืนยันการเปิดกล่องสมบัติ</p>
                            <p class="text-xxs text-indigo-300">สามารถเปิดได้วันละครั้ง และจะรีเซ็ตเวลา 21:00 น. (เวลาไทย)</p>
                          </div>
                        </div>
                        <div class="mt-3 flex items-center gap-2">
                          <button
                            @click="openTreasureBox"
                            class="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs border border-indigo-500"
                          >
                            ยืนยันเปิด
                          </button>
                          <button
                            @click="confirmOpen = false"
                            class="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-indigo-200 text-xs border border-gray-600"
                          >
                            ยกเลิก
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                  <!-- Resign Action -->
                  <div v-if="isCurrentUserOccupier" class="relative">
                    <!-- Primary action row -->
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                      <div class="flex items-center gap-2">
                        <button
                          v-if="!isResigning"
                          @click="confirmResign = true"
                          class="group inline-flex items-center gap-3 px-4 py-2 rounded-md transition-colors duration-200
                                 focus:outline-none border
                                 bg-red-600 hover:bg-red-500 text-white border-red-500"
                          aria-label="ถอนตัวจากการยึดครอง"
                          @mouseenter="showHintResign = true"
                          @mouseleave="showHintResign = false"
                        >
                          <i class="fas fa-flag text-lg"></i>
                          <div class="flex flex-col items-start leading-tight">
                            <span class="font-semibold text-sm">ถอนตัวจากการยึดครอง</span>
                            <span class="text-xxs opacity-80">สละสถานะผู้ยึดครองทันที</span>
                          </div>
                        </button>
                        <!-- Loading state -->
                        <div
                          v-else
                          class="inline-flex items-center gap-3 px-4 py-2 rounded-md bg-red-600 text-white border border-red-500"
                        >
                          <i class="fas fa-flag animate-shake text-yellow-300 text-xl"></i>
                          <span class="animate-pulse text-sm">กำลังถอนตัว...</span>
                        </div>
                        <!-- Tiny hover hint -->
                        <div
                          v-if="showHintResign && !isResigning"
                          class="hidden sm:block text-xxs bg-gray-900 border border-gray-700 px-2 py-1 rounded-md"
                        >
                          หลังถอนตัวจะไม่ได้รับรางวัลรายวัน
                        </div>
                      </div>
                      <!-- Right info side -->
                      <div class="text-xxs sm:text-xs text-indigo-300">
                        <p v-if="resignMessage" :class="resignMessage.includes('สำเร็จ') ? 'text-green-400' : 'text-red-400'">
                          {{ resignMessage }}
                        </p>
                      </div>
                    </div>
                    <!-- Step 2: Confirm panel -->
                    <transition name="fade">
                      <div
                        v-if="confirmResign"
                        class="mt-2 p-3 rounded-md border bg-gray-900 border-gray-700"
                      >
                        <div class="flex items-start gap-3">
                          <i class="fas fa-exclamation-triangle text-red-400 mt-0.5"></i>
                          <div class="flex-1">
                            <p class="text-sm text-indigo-200 font-medium">ยืนยันการถอนตัวจากการยึดครอง</p>
                            <p class="text-xxs text-indigo-300">คุณจะสละตำแหน่งและหยุดรับรางวัลรายวันทันที</p>
                          </div>
                        </div>
                        <div class="mt-3 flex items-center gap-2">
                          <button
                            @click="resignOccupier"
                            class="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-xs border border-red-500"
                          >
                            ยืนยันถอนตัว
                          </button>
                          <button
                            @click="confirmResign = false"
                            class="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-indigo-200 text-xs border border-gray-600"
                          >
                            ยกเลิก
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
                <!-- ===== /Enhanced Actions ===== -->
              </div>
            </div>
          </div>
        </div>
        <!-- Zombies Tab -->
        <div v-if="activeTab === 'zombies'" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300">ซอมบี้ในแผนที่</h3>
          <div v-if="sortedZombies.length > 0" class="space-y-2">
            <div
              v-for="zombie in sortedZombies"
              :key="zombie.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showZombieDetails(zombie.name)"
            >
              <div class="relative">
                <img
                  v-if="zombieDetails[zombie.name]?.imageUrl"
                  :src="zombieDetails[zombie.name].imageUrl"
                  :alt="zombie.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingZombie[zombie.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-indigo-200 font-medium text-sm">{{ zombie.name }}</p>
                <p class="text-indigo-300 text-xs">โอกาส: {{ zombie.chance }}%</p>
                <p class="text-indigo-300 text-xs">ประเภท: {{ translateZombieType(zombieDetails[zombie.name]?.type) }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูล</p>
        </div>
        <!-- Merchants Tab -->
        <div v-if="activeTab === 'merchants'" class="space-y-3 animate-fadeIn">
          <h3 class="text-sm font-semibold text-indigo-300">พ่อค้าเร่ในแผนที่</h3>
          <div v-if="sortedMerchants.length > 0" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
            <div
              v-for="merchant in sortedMerchants"
              :key="merchant.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showMerchantDetails(merchant.name)"
            >
              <div class="relative">
                <img
                  v-if="merchantDetails[merchant.name]?.img"
                  :src="merchantDetails[merchant.name].img"
                  :alt="merchant.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingMerchant[merchant.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ merchantDetails[merchant.name]?.rarity || 'N/A' }}</span>
                  </div>
                  <span>{{ merchant.name }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (merchant.chance / 100).toFixed(2) }}%</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลพ่อค้าเร่</p>
        </div>
        <!-- Mutants Tab -->
        <div v-if="activeTab === 'mutants'" class="space-y-3 animate-fadeIn">
          <h3 class="text-sm font-semibold text-indigo-300">สิ่งมีชีวิตกลายพันธุ์ในแผนที่</h3>
          <div v-if="sortedMutants.length > 0" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
            <div
              v-for="mutant in sortedMutants"
              :key="mutant.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showMutantDetails(mutant.name)"
            >
              <div class="relative">
                <img
                  v-if="mutantDetails[mutant.name]?.img"
                  :src="mutantDetails[mutant.name].img"
                  :alt="mutant.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingMutant[mutant.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ mutantDetails[mutant.name]?.rarity || 'N/A' }}</span>
                  </div>
                  <span>{{ mutant.name }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (mutant.chance / 100).toFixed(2) }}%</p>
                <p class="text-indigo-300 text-xs">สามารถจับได้: {{ mutantDetails[mutant.name]?.canCatch ? 'ใช่' : 'ไม่' }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลสิ่งมีชีวิตกลายพันธุ์</p>
        </div>
        <!-- Villagers Tab -->
        <div v-if="activeTab === 'villagers'" class="space-y-3 animate-fadeIn">
          <h3 class="text-sm font-semibold text-indigo-300">ชาวบ้านในแผนที่</h3>
          <div v-if="sortedVillagers.length > 0" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
            <div
              v-for="villager in sortedVillagers"
              :key="villager.name"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showVillagerDetails(villager.name)"
            >
              <div class="relative">
                <img
                  v-if="villagerDetails[villager.name]?.img"
                  :src="villagerDetails[villager.name].img"
                  :alt="villager.name"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="isLoadingVillager[villager.name]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ villagerDetails[villager.name]?.level || 'N/A' }}</span>
                  </div>
                  <span>{{ villager.name }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (villager.chance / 100).toFixed(2) }}%</p>
                <p class="text-indigo-300 text-xs">จำนวนลูกเต๋า: ฝ่ายบุก <strong>{{ villagerDetails[villager.name]?.attackDice || 0 }}</strong> | ฝ่ายตั้งรับ <strong>{{ villagerDetails[villager.name]?.defenseDice || 0 }}</strong></p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลชาวบ้าน</p>
        </div>
        <!-- Rewards Tab -->
        <div v-if="activeTab === 'rewards'" class="space-y-2 h-72 overflow-auto pb-20" id="style-1">
          <!-- Diamond Drop -->
          <div class="bg-gray-800 rounded-md p-4 border border-gray-700">
            <h4 class="text-xs sm:text-sm font-semibold text-indigo-300 mb-2">รางวัลเพชร</h4>
            <div class="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm text-indigo-200 gap-1">
              <span>ขั้นต่ำ: {{ map.diamondDropMin || 0 }}</span>
              <span>ขั้นสูงสุด: {{ map.diamondDropMax || 0 }}</span>
              <span>โอกาสได้รับ: {{ map.diamondDropChance || 0 }}%</span>
            </div>
          </div>
          <!-- Filter -->
          <div class="flex items-center gap-2">
            <label for="itemTypeFilter" class="text-indigo-200 text-xs sm:text-sm">ประเภทไอเทม:</label>
            <select
              id="itemTypeFilter"
              v-model="selectedItemType"
              class="bg-gray-800 text-indigo-200 border border-gray-600 rounded-md px-2 py-1 text-xs sm:text-sm focus:outline-none"
            >
              <option value="">ทั้งหมด</option>
              <option value="wearable">ไอเทมสวมใส่</option>
              <option value="book">หนังสือ</option>
              <option value="general">ทั่วไป</option>
              <option value="food">อาหาร</option>
              <option value="map">แผนที่</option>
              <option value="heal">รักษา</option>
              <option value="mechanical_organ">อวัยวะจักรกล</option>
            </select>
          </div>
          <!-- Reward List -->
          <div v-if="filteredRewards.length > 0" class="space-y-2">
            <div
              v-for="reward in filteredRewards"
              :key="reward.itemName"
              class="flex items-center gap-3 p-3 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 border border-gray-600"
              @click="showItemDetails(reward.itemName)"
            >
              <div class="relative">
                <img
                  v-if="itemDetails[reward.itemName]?.img"
                  :src="itemDetails[reward.itemName].img"
                  :alt="reward.itemName"
                  class="w-10 h-10 object-cover rounded-full border border-gray-600"
                  :class="[reward.rarityColors?.borderColor || 'border-gray-600']"
                  @error="handleImageError"
                />
                <div v-if="isLoadingItem[reward.itemName]" class="absolute inset-0 flex items-center justify-center">
                  <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 text-indigo-200 text-sm">
                  <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                    <img src="../assets/star.png" class="w-3 mr-1" />
                    <span>{{ itemDetails[reward.itemName]?.rarity || 'N/A' }}</span>
                  </div>
                  <span>{{ reward.itemName }}</span>
                </div>
                <p class="text-indigo-300 text-xs">โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%</p>
                <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[reward.itemName]?.type || 'N/A' }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลรางวัล</p>
        </div>
        <!-- Situations Tab -->
        <div v-if="activeTab === 'situations'" class="space-y-3 h-72 overflow-auto pb-20" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300">สถานการณ์ในแผนที่</h3>
          <div v-if="sortedSituations.length > 0" class="space-y-2">
            <div
              v-for="situation in sortedSituations"
              :key="situation.name"
              class="p-3 bg-gray-800 rounded-md border border-gray-600"
            >
              <div class="flex items-center gap-3 mb-2">
                <div class="flex-1">
                  <p class="text-indigo-200 font-medium text-sm">{{ situation.name }}</p>
                  <p class="text-indigo-300 text-xs">โอกาส: {{ situation.chance }}%</p>
                  <p class="text-indigo-300 text-xs">ซอมบี้: {{ situation.minZombies }}-{{ situation.maxZombies }}</p>
                </div>
              </div>
              <div v-if="situation.rewards && situation.rewards.length > 0" class="space-y-2">
                <p class="text-indigo-300 text-xs font-medium">รางวัล:</p>
                <div
                  v-for="reward in situation.rewards"
                  :key="reward.itemName"
                  class="flex items-center gap-3 p-2 bg-gray-700 rounded-md cursor-pointer hover:bg-gray-600 transition-all duration-200 border border-gray-600"
                  @click="showItemDetails(reward.itemName)"
                >
                  <div class="relative">
                    <img
                      v-if="itemDetails[reward.itemName]?.img"
                      :src="itemDetails[reward.itemName].img"
                      :alt="reward.itemName"
                      class="w-8 h-8 object-cover rounded-full border border-gray-600"
                      :class="[reward.rarityColors?.borderColor || 'border-gray-600']"
                      @error="handleImageError"
                    />
                    <div v-if="isLoadingItem[reward.itemName]" class="absolute inset-0 flex items-center justify-center">
                      <div class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2 text-indigo-200 text-xs">
                      <div class="flex items-center text-xs rounded border border-gray-600 bg-gray-600 px-1.5 py-0.5">
                        <img src="../assets/star.png" class="w-3 mr-1" />
                        <span>{{ itemDetails[reward.itemName]?.rarity || 'N/A' }}</span>
                      </div>
                      <span>{{ reward.itemName }}</span>
                    </div>
                    <p class="text-indigo-300 text-xs">โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%</p>
                    <p class="text-indigo-300 text-xs">ประเภท: {{ itemDetails[reward.itemName]?.type || 'N/A' }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-indigo-300 text-xs">ไม่มีรางวัลสำหรับสถานการณ์นี้</p>
            </div>
          </div>
          <p v-else class="text-indigo-300 text-xs">ไม่มีข้อมูลสถานการณ์</p>
        </div>
        <!-- Zombie Details Modal -->
        <div
          v-if="selectedZombie"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedZombie.name }}</h3>
              <button @click="selectedZombie = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="zombieDetails[selectedZombie.name]?.imageUrl"
                  :src="zombieDetails[selectedZombie.name].imageUrl"
                  :alt="selectedZombie.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="zombieDetails[selectedZombie.name]?.level">
                  <span class="font-medium">เลเวล:</span> {{ zombieDetails[selectedZombie.name]?.level || 'N/A' }}
                </div>
              </div>
              <div v-if="hasNonZeroStats(zombieDetails[selectedZombie.name]?.stats)">
                <span class="font-medium">Stats:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.stamina > 0">Stamina: {{ zombieDetails[selectedZombie.name]?.stats?.stamina }}</li>
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.str > 0">Strength: {{ zombieDetails[selectedZombie.name]?.stats?.str }}</li>
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.int > 0">Intelligence: {{ zombieDetails[selectedZombie.name]?.stats?.int }}</li>
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.agi > 0">Agility: {{ zombieDetails[selectedZombie.name]?.stats?.agi }}</li>
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.luck > 0">Luck: {{ zombieDetails[selectedZombie.name]?.stats?.luck }}</li>
                  <li v-if="zombieDetails[selectedZombie.name]?.stats?.defense > 0">Defense: {{ zombieDetails[selectedZombie.name]?.stats?.defense }}</li>
                </ul>
              </div>
              <div v-if="zombieDetails[selectedZombie.name]?.infectionChance > 0">
                <span class="font-medium">โอกาสติดเชื้อ:</span> {{ zombieDetails[selectedZombie.name]?.infectionChance }}%
              </div>
              <div v-if="zombieDetails[selectedZombie.name]?.expReward > 0">
                <span class="font-medium">EXP Reward:</span> {{ zombieDetails[selectedZombie.name]?.expReward }}
              </div>
              <div v-if="zombieDetails[selectedZombie.name]?.silverReward > 0">
                <span class="font-medium">Silver Reward:</span> {{ zombieDetails[selectedZombie.name]?.silverReward }}
              </div>
            </div>
          </div>
        </div>
        <!-- Merchant Details Modal -->
        <div
          v-if="selectedMerchant"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedMerchant.name }}</h3>
              <button @click="selectedMerchant = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="merchantDetails[selectedMerchant.name]?.img"
                  :src="merchantDetails[selectedMerchant.name].img"
                  :alt="selectedMerchant.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="merchantDetails[selectedMerchant.name]?.rarity > 0"><span class="font-medium">ระดับความหายาก:</span> {{ getStars(merchantDetails[selectedMerchant.name]?.rarity) }}</div>
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.items?.length > 0">
                <span class="font-medium">สินค้าที่ขาย:</span>
                <ul class="list-disc ml-4 text-xs space-y-2">
                  <li v-for="(item, index) in merchantDetails[selectedMerchant.name]?.items" :key="index" class="flex items-center gap-2">
                    <img
                      v-if="itemDetails[item.name]?.img"
                      :src="itemDetails[item.name].img"
                      :alt="item.name"
                      class="w-6 h-6 object-cover rounded-full border border-gray-600"
                      @error="handleImageError"
                    />
                    <span>{{ item.name }}: {{ item.price }}</span>
                    <i v-if="item.currency === 'diamond'" class="fas fa-gem text-blue-400"></i>
                    <i v-else-if="item.currency === 'gold'" class="fas fa-coins text-yellow-400"></i>
                    <i v-else class="fas fa-coins text-gray-200"></i>
                  </li>
                </ul>
              </div>
              <div>
                <span class="font-medium">อนุญาตให้ต่อสู้เพื่อปล้น:</span> {{ merchantDetails[selectedMerchant.name]?.allowRob ? 'ใช่' : 'ไม่' }}
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.allowRob">
                <span class="font-medium">รางวัลจากการปล้น:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li>Silver: {{ merchantDetails[selectedMerchant.name]?.robRewards?.silver || 0 }}</li>
                  <li>Gold: {{ merchantDetails[selectedMerchant.name]?.robRewards?.gold || 0 }}</li>
                  <li>Diamond: {{ merchantDetails[selectedMerchant.name]?.robRewards?.diamond || 0 }}</li>
                </ul>
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.normalFace">
                <span class="font-medium">รูปหน้าปกติ:</span>
                <img :src="merchantDetails[selectedMerchant.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
              <div v-if="merchantDetails[selectedMerchant.name]?.angryFace">
                <span class="font-medium">รูปหน้าที่โมโห:</span>
                <img :src="merchantDetails[selectedMerchant.name].angryFace" alt="Angry Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
              <div class="space-y-1">
                <span class="font-medium">ค่าสถานะ:</span>
                <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <div class="flex items-center space-x-1"><i class="fas fa-heart text-red-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.stamina || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-dumbbell text-yellow-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.str || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-brain text-blue-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.int || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-running text-green-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.agi || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-clover text-green-300"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.luck || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-shield-alt text-purple-500"></i><span>{{ merchantDetails[selectedMerchant.name]?.stats?.defense || 0 }}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Mutant Details Modal -->
        <div
          v-if="selectedMutant"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedMutant.name }}</h3>
              <button @click="selectedMutant = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="mutantDetails[selectedMutant.name]?.img"
                  :src="mutantDetails[selectedMutant.name].img"
                  :alt="selectedMutant.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="mutantDetails[selectedMutant.name]?.rarity > 0"><span class="font-medium">ระดับความหายาก:</span> {{ getStars(mutantDetails[selectedMutant.name]?.rarity) }}</div>
              </div>
              <div v-if="mutantDetails[selectedMutant.name]?.stats">
                <span class="font-medium">ค่าสถานะ:</span>
                <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <div class="flex items-center space-x-1"><i class="fas fa-heart text-red-500"></i><span>{{ mutantDetails[selectedMutant.name].stats.stamina || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-dumbbell text-yellow-500"></i><span>{{ mutantDetails[selectedMutant.name].stats.str || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-brain text-blue-500"></i><span>{{ mutantDetails[selectedMutant.name].stats.int || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-running text-green-500"></i><span>{{ mutantDetails[selectedMutant.name].stats.agi || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-clover text-green-300"></i><span>{{ mutantDetails[selectedMutant.name].stats.luck || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-shield-alt text-purple-500"></i><span>{{ mutantDetails[selectedMutant.name].stats.defense || 0 }}</span></div>
                </div>
              </div>
              <div>
                <span class="font-medium">สามารถจับได้:</span> {{ mutantDetails[selectedMutant.name]?.canCatch ? 'ใช่' : 'ไม่' }}
              </div>
              <div v-if="mutantDetails[selectedMutant.name]?.canCatch">
                <span class="font-medium">ระดับความยากในการจับ:</span> {{ mutantDetails[selectedMutant.name]?.catchDifficulty || 1 }}
                <p class="ml-4 text-xs">จำนวนครั้งที่สามารถจับได้: {{ mutantDetails[selectedMutant.name]?.catchLimit || 0 }}</p>
              </div>
              <div v-if="mutantDetails[selectedMutant.name]?.normalFace">
                <span class="font-medium">รูปหน้าปกติ:</span>
                <img :src="mutantDetails[selectedMutant.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
            </div>
          </div>
        </div>
        <!-- Villager Details Modal -->
        <div
          v-if="selectedVillager"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedVillager.name }}</h3>
              <button @click="selectedVillager = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="villagerDetails[selectedVillager.name]?.img"
                  :src="villagerDetails[selectedVillager.name].img"
                  :alt="selectedVillager.name"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  @error="handleImageError"
                />
                <div v-if="villagerDetails[selectedVillager.name]?.level > 0"><span class="font-medium">เลเวล:</span> {{ getStars(villagerDetails[selectedVillager.name]?.level) }}</div>
              </div>
              <div v-if="villagerDetails[selectedVillager.name]?.hireCosts">
                <span class="font-medium">ค่าใช้จ่ายในการจ้าง:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li>Silver: {{ villagerDetails[selectedVillager.name].hireCosts.silver || 0 }}</li>
                  <li>Gold: {{ villagerDetails[selectedVillager.name].hireCosts.gold || 0 }}</li>
                  <li>Diamond: {{ villagerDetails[selectedVillager.name].hireCosts.diamond || 0 }}</li>
                </ul>
              </div>
              <div v-if="villagerDetails[selectedVillager.name]?.stats">
                <span class="font-medium">ค่าสถานะ:</span>
                <div class="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <div class="flex items-center space-x-1"><i class="fas fa-heart text-red-500"></i><span>{{ villagerDetails[selectedVillager.name].stats.stamina || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-dumbbell text-yellow-500"></i><span>{{ villagerDetails[selectedVillager.name].stats.str || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-brain text-blue-500"></i><span>{{ villagerDetails[selectedVillager.name].stats.int || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-running text-green-500"></i><span>{{ villagerDetails[selectedVillager.name].stats.agi || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-clover text-green-300"></i><span>{{ villagerDetails[selectedVillager.name].stats.luck || 0 }}</span></div>
                  <div class="flex items-center space-x-1"><i class="fas fa-shield-alt text-purple-500"></i><span>{{ villagerDetails[selectedVillager.name].stats.defense || 0 }}</span></div>
                </div>
              </div>
              <div v-if="villagerDetails[selectedVillager.name]?.attackDice || villagerDetails[selectedVillager.name]?.defenseDice">
                <span class="font-medium">จำนวนลูกเต๋า:</span>
                <p class="ml-4 text-xs">บุก: {{ villagerDetails[selectedVillager.name]?.attackDice || 0 }} | ตั้งรับ: {{ villagerDetails[selectedVillager.name]?.defenseDice || 0 }}</p>
              </div>
              <div v-if="villagerDetails[selectedVillager.name]?.normalFace">
                <span class="font-medium">รูปหน้าปกติ:</span>
                <img :src="villagerDetails[selectedVillager.name].normalFace" alt="Normal Face" class="w-8 h-8 object-cover rounded-full inline-block ml-2 border border-gray-600" @error="handleImageError" />
              </div>
            </div>
          </div>
        </div>
        <!-- Item Details Modal -->
        <div
          v-if="selectedItem"
          class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn" style="max-height:70vh; overflow:auto;">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedItem.itemName }}</h3>
              <button @click="selectedItem = null" class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2" aria-label="ปิดหน้าต่างรายละเอียดไอเทม">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="itemDetails[selectedItem.itemName]?.img"
                  :src="itemDetails[selectedItem.itemName].img"
                  :alt="selectedItem.itemName"
                  class="w-12 h-12 object-cover rounded-full border border-gray-600"
                  :class="[selectedItem.rarityColors?.borderColor || 'border-gray-600']"
                  @error="handleImageError"
                />
                <div>
                  <div v-if="itemDetails[selectedItem.itemName]?.type"><span class="font-medium">ประเภท:</span> {{ itemDetails[selectedItem.itemName]?.type || 'N/A' }}</div>
                  <div v-if="itemDetails[selectedItem.itemName]?.rarity"><span class="font-medium">ความหายาก:</span> {{ itemDetails[selectedItem.itemName]?.rarity || 'N/A' }}</div>
                </div>
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.requiredLevel > 0">
                <span class="font-medium">เลเวลขั้นต่ำ:</span> {{ itemDetails[selectedItem.itemName]?.requiredLevel }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.weight > 0">
                <span class="font-medium">น้ำหนัก:</span> {{ itemDetails[selectedItem.itemName]?.weight }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.price > 0">
                <span class="font-medium">ราคา:</span> {{ itemDetails[selectedItem.itemName]?.price }}
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.description && itemDetails[selectedItem.itemName]?.description !== 'ไม่มี'">
                <span class="font-medium">คำอธิบาย:</span> {{ itemDetails[selectedItem.itemName]?.description }}
              </div>
              <div v-if="hasNonZeroStats(itemDetails[selectedItem.itemName]?.stats)">
                <span class="font-medium">Stats:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.stamina > 0">Stamina: {{ itemDetails[selectedItem.itemName]?.stats?.stamina }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.str > 0">Strength: {{ itemDetails[selectedItem.itemName]?.stats?.str }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.int > 0">Intelligence: {{ itemDetails[selectedItem.itemName]?.stats?.int }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.agi > 0">Agility: {{ itemDetails[selectedItem.itemName]?.stats?.agi }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.luck > 0">Luck: {{ itemDetails[selectedItem.itemName]?.stats?.luck }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.maxHp > 0">Max HP: {{ itemDetails[selectedItem.itemName]?.stats?.maxHp }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.atk > 0">Attack: {{ itemDetails[selectedItem.itemName]?.stats?.atk }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.evasion > 0">Evasion: {{ itemDetails[selectedItem.itemName]?.stats?.evasion }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.critical > 0">Critical: {{ itemDetails[selectedItem.itemName]?.stats?.critical }}</li>
                  <li v-if="itemDetails[selectedItem.itemName]?.stats?.defense > 0">Defense: {{ itemDetails[selectedItem.itemName]?.stats?.defense }}</li>
                </ul>
              </div>
              <div v-if="itemDetails[selectedItem.itemName]?.type === 'รักษา' && itemDetails[selectedItem.itemName]?.healHpPercentage > 0">
                <span class="font-medium">HP ที่ได้รับ:</span> {{ itemDetails[selectedItem.itemName]?.healHpPercentage }}%
              </div>
              <div>
                <span class="font-medium">แยกส่วนได้:</span> {{ itemDetails[selectedItem.itemName]?.decomposable === true ? 'ใช่' : 'ไม่' }}
                <div v-if="itemDetails[selectedItem.itemName]?.decomposable === true" class="ml-4 mt-1 space-y-1">
                  <div v-if="itemDetails[selectedItem.itemName]?.decomposeList && itemDetails[selectedItem.itemName].decomposeList.length > 0" class="text-xs text-indigo-300">
                    <p class="font-medium">รางวัลจากการแยกส่วน:</p>
                    <ul class="list-disc ml-3 space-y-0.5">
                      <li v-for="(decompose, index) in itemDetails[selectedItem.itemName].decomposeList" :key="index">
                        {{ decompose.itemName || 'N/A' }}: {{ decompose.chance || 0 }}%
                      </li>
                    </ul>
                  </div>
                  <p v-else class="text-xs text-indigo-300">ไม่มีรายละเอียดรางวัล</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, watch, computed, onUnmounted } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import gameItemsData from '@/data/gameItems.json';
import gameZombiesData from '@/data/gameZombies.json';
import gameMerchantsData from '@/data/gameMerchants.json';
import gameMutantsData from '@/data/gameMutants.json';
import gameVillagersData from '@/data/gameVillagers.json';
export default {
  props: {
    map: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    // -------------------- Tabs & base states --------------------
    const activeTab = ref('mapInfo');
    // Messages / status
    const treasureMessage = ref('');
    const resignMessage = ref('');
    const countdownText = ref('');
    const treasureCooldownText = ref('');
    // Loading flags
    const isOpeningTreasure = ref(false);
    const isResigning = ref(false);
    // 2-step confirm + hover hints
    const confirmOpen = ref(false);
    const confirmResign = ref(false);
    const showHintOpen = ref(false);
    const showHintResign = ref(false);
    const currentTime = ref(Date.now());
    let countdownInterval = null;
    let cooldownInterval = null;
    // -------------------- Tabs visibility --------------------
    const baseTabs = [
      { id: 'mapInfo', name: 'ข้อมูลแผนที่', tooltip: 'ดูข้อมูลพื้นฐานของแผนที่' },
      { id: 'zombies', name: 'ซอมบี้', tooltip: 'ดูรายการซอมบี้ในแผนที่' },
      { id: 'rewards', name: 'รางวัล', tooltip: 'ดูรางวัลที่ได้รับจากแผนที่' },
      { id: 'situations', name: 'สถานการณ์', tooltip: 'ดูสถานการณ์ที่อาจเกิดขึ้นในแผนที่' },
      { id: 'merchants', name: 'พ่อค้าเร่', tooltip: 'ดูพ่อค้าเร่ในแผนที่' },
      { id: 'mutants', name: 'สิ่งมีชีวิตกลายพันธุ์', tooltip: 'ดูสิ่งมีชีวิตกลายพันธุ์ในแผนที่' },
      { id: 'villagers', name: 'ชาวบ้าน', tooltip: 'ดูชาวบ้านในแผนที่' },
    ];
    const hasSituations = computed(() => Array.isArray(props.map.situations) && props.map.situations.length > 0);
    const hasMerchants = computed(() => Array.isArray(props.map.merchants) && props.map.merchants.length > 0);
    const hasMutants = computed(() => Array.isArray(props.map.mutants) && props.map.mutants.length > 0);
    const hasVillagers = computed(() => Array.isArray(props.map.villagers) && props.map.villagers.length > 0);
    const tabs = computed(() =>
      baseTabs.filter(tab =>
        (tab.id !== 'situations' || hasSituations.value) &&
        (tab.id !== 'merchants' || hasMerchants.value) &&
        (tab.id !== 'mutants' || hasMutants.value) &&
        (tab.id !== 'villagers' || hasVillagers.value)
      )
    );
    // -------------------- Data caches --------------------
    const zombieDetails = ref({});
    const merchantDetails = ref({});
    const mutantDetails = ref({});
    const villagerDetails = ref({});
    const itemDetails = ref({});
    const occupierDetails = ref({ imageUrl: '', characterName: '', level: 0, room: 0 });
    // Loading state maps
    const isLoadingZombie = ref({});
    const isLoadingMerchant = ref({});
    const isLoadingMutant = ref({});
    const isLoadingVillager = ref({});
    const isLoadingItem = ref({});
    const isLoadingOccupier = ref(false);
    // Selections
    const selectedZombie = ref(null);
    const selectedMerchant = ref(null);
    const selectedMutant = ref(null);
    const selectedVillager = ref(null);
    const selectedItem = ref(null);
    const selectedItemType = ref('');
    // User
    const { user } = getUser();
    const isCurrentUserOccupier = computed(() => user.value && props.map.occupierUid === user.value.uid);
    // Wearable types
    const wearableTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];
    // -------------------- Sorters / filters --------------------
    const sortedZombies = computed(() => [...(props.map.zombies || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedMerchants = computed(() => [...(props.map.merchants || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedMutants = computed(() => [...(props.map.mutants || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedVillagers = computed(() => [...(props.map.villagers || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const sortedRewards = computed(() => [...(props.map.rewards || [])].sort((a, b) => (b.dropChance || 0) - (a.dropChance || 0)));
    const sortedSituations = computed(() => [...(props.map.situations || [])].sort((a, b) => (b.chance || 0) - (a.chance || 0)));
    const filteredRewards = computed(() => {
      if (!selectedItemType.value) return sortedRewards.value;
      return sortedRewards.value.filter(reward => {
        const item = itemDetails.value[reward.itemName];
        if (!item) return false;
        if (selectedItemType.value === 'wearable') return wearableTypes.includes(item.type);
        if (selectedItemType.value === 'book') return item.type === 'หนังสือ';
        if (selectedItemType.value === 'general') return item.type === 'ทั่วไป';
        if (selectedItemType.value === 'food') return item.type === 'อาหาร';
        if (selectedItemType.value === 'map') return item.type === 'แผนที่';
        if (selectedItemType.value === 'heal') return item.type === 'รักษา';
        if (selectedItemType.value === 'mechanical_organ') return item.type === 'อวัยวะจักรกล';
        return false;
      });
    });
    // -------------------- Helpers --------------------
    const translateZombieType = (type) => {
      if (type === 'normal') return 'ทั่วไป';
      if (type === 'boss') return 'บอส';
      return 'N/A';
    };
    const hasNonZeroStats = (stats) => stats && Object.values(stats).some(v => v > 0);
    const getStars = (level) => {
      const numStars = Number(level) || 0;
      return '★'.repeat(numStars);
    };
    // -------------------- Firestore fetchers --------------------
    const fetchZombieDetails = async (zombieName) => {
      if (!zombieDetails.value[zombieName] && !isLoadingZombie.value[zombieName]) {
        try {
          isLoadingZombie.value[zombieName] = true;
          zombieDetails.value[zombieName] = gameZombiesData[zombieName] || null;
        } catch (e) {
          zombieDetails.value[zombieName] = null;
        } finally {
          isLoadingZombie.value[zombieName] = false;
        }
      }
    };
    const fetchMerchantDetails = async (merchantName) => {
      if (!merchantDetails.value[merchantName] && !isLoadingMerchant.value[merchantName]) {
        try {
          isLoadingMerchant.value[merchantName] = true;
          merchantDetails.value[merchantName] = gameMerchantsData[merchantName] || null;
        } catch (e) {
          merchantDetails.value[merchantName] = null;
        } finally {
          isLoadingMerchant.value[merchantName] = false;
        }
      }
    };
    const fetchMutantDetails = async (mutantName) => {
      if (!mutantDetails.value[mutantName] && !isLoadingMutant.value[mutantName]) {
        try {
          isLoadingMutant.value[mutantName] = true;
          mutantDetails.value[mutantName] = gameMutantsData[mutantName] || null;
        } catch (e) {
          mutantDetails.value[mutantName] = null;
        } finally {
          isLoadingMutant.value[mutantName] = false;
        }
      }
    };
    const fetchVillagerDetails = async (villagerName) => {
      if (!villagerDetails.value[villagerName] && !isLoadingVillager.value[villagerName]) {
        try {
          isLoadingVillager.value[villagerName] = true;
          villagerDetails.value[villagerName] = gameVillagersData[villagerName] || null;
        } catch (e) {
          villagerDetails.value[villagerName] = null;
        } finally {
          isLoadingVillager.value[villagerName] = false;
        }
      }
    };
    const fetchItemDetails = (itemName) => {
      if (!itemDetails.value[itemName] && !isLoadingItem.value[itemName]) {
        try {
          isLoadingItem.value[itemName] = true;
          itemDetails.value[itemName] = gameItemsData[itemName] || null;
        } catch (e) {
          itemDetails.value[itemName] = null;
        } finally {
          isLoadingItem.value[itemName] = false;
        }
      }
    };
    const fetchOccupierDetails = async (uid) => {
      if (!uid) return;
      try {
        isLoadingOccupier.value = true;
        const characterDoc = await projectFirestore.collection('characters').doc(uid).get();
        if (characterDoc.exists) {
          const data = characterDoc.data();
          occupierDetails.value = {
            imageUrl: data.imageUrl || '',
            characterName: data.characterName || 'N/A',
            level: data.level || 0,
            room: data.room || 0
          };
        } else {
          occupierDetails.value = { imageUrl: '', characterName: 'N/A', level: 0, room: 0 };
        }
      } catch (error) {
        console.error('Error fetching occupier details:', error);
        occupierDetails.value = { imageUrl: '', characterName: 'N/A', level: 0, room: 0 };
      } finally {
        isLoadingOccupier.value = false;
      }
    };
    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };
    // -------------------- Show detail modals --------------------
    const showZombieDetails = async (zombieName) => {
      await fetchZombieDetails(zombieName);
      const zombie = (props.map.zombies || []).find(z => z.name === zombieName);
      if (zombie && zombieDetails.value[zombieName]) {
        selectedZombie.value = zombie;
      }
    };
    const showMerchantDetails = async (merchantName) => {
      await fetchMerchantDetails(merchantName);
      const merchant = (props.map.merchants || []).find(m => m.name === merchantName);
      if (merchant && merchantDetails.value[merchantName]) {
        if (merchantDetails.value[merchantName]?.items?.length > 0) {
          merchantDetails.value[merchantName].items.forEach(item => fetchItemDetails(item.name));
        }
        selectedMerchant.value = merchant;
      }
    };
    const showMutantDetails = async (mutantName) => {
      await fetchMutantDetails(mutantName);
      const mutant = (props.map.mutants || []).find(m => m.name === mutantName);
      if (mutant && mutantDetails.value[mutantName]) {
        selectedMutant.value = mutant;
      }
    };
    const showVillagerDetails = async (villagerName) => {
      await fetchVillagerDetails(villagerName);
      const villager = (props.map.villagers || []).find(v => v.name === villagerName);
      if (villager && villagerDetails.value[villagerName]) {
        selectedVillager.value = villager;
      }
    };
    const showItemDetails = async (itemName) => {
      fetchItemDetails(itemName);
      const item = (props.map.rewards || []).find(r => r.itemName === itemName) ||
                   (props.map.situations || []).flatMap(s => s.rewards || []).find(r => r.itemName === itemName);
      if (item && itemDetails.value[itemName]) {
        selectedItem.value = item;
      }
    };
    // -------------------- Actions: treasure / resign --------------------
    const openTreasureBox = async () => {
      if (!props.map.name) {
        treasureMessage.value = 'ไม่สามารถเปิดกล่องสมบัติได้: ไม่พบชื่อแผนที่';
        confirmOpen.value = false;
        return;
      }
      isOpeningTreasure.value = true;
      treasureMessage.value = '';
      try {
        const minDelay = new Promise(resolve => setTimeout(resolve, 2500));
        const [response] = await Promise.all([
          projectFunctions.httpsCallable('gameActions')({
            action: 'occupierActions',
            subAction: 'openTreasureBox',
            mapName: props.map.name
          }),
          minDelay
        ]);
        treasureMessage.value = response.data.message;
        confirmOpen.value = false;
      } catch (error) {
        console.error('Error opening treasure box:', error);
        treasureMessage.value = `ไม่สามารถเปิดกล่องสมบัติได้: ${error.message}`;
      } finally {
        isOpeningTreasure.value = false;
      }
    };
    const resignOccupier = async () => {
      if (!props.map.name) {
        resignMessage.value = 'ไม่สามารถถอนตัวจากการยึดครองได้: ไม่พบชื่อแผนที่';
        confirmResign.value = false;
        return;
      }
      isResigning.value = true;
      resignMessage.value = '';
      try {
        const minDelay = new Promise(resolve => setTimeout(resolve, 2500));
        const [response] = await Promise.all([
          projectFunctions.httpsCallable('gameActions')({
            action: 'occupierActions',
            subAction: 'resignOccupier',
            mapName: props.map.name
          }),
          minDelay
        ]);
        resignMessage.value = response.data.message;
        confirmResign.value = false;
        emit('close');
      } catch (error) {
        console.error('Error resigning from occupation:', error);
        resignMessage.value = `ไม่สามารถถอนตัวจากการยึดครองได้: ${error.message}`;
      } finally {
        isResigning.value = false;
      }
    };
    // -------------------- Daily reset countdown (21:00 Asia/Bangkok) --------------------
    const updateCountdown = () => {
      const now = new Date();
      const utcNow = now.getTime() + now.getTimezoneOffset() * 60000;
      const thaiNow = new Date(utcNow + 7 * 3600000);
      const year = thaiNow.getFullYear();
      const month = thaiNow.getMonth();
      const day = thaiNow.getDate();
      let resetTime = new Date(year, month, day, 21, 0, 0);
      if (thaiNow >= resetTime) {
        resetTime = new Date(year, month, day + 1, 21, 0, 0);
      }
      const diff = resetTime - thaiNow;
      if (diff > 0) {
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        countdownText.value = `เหลือเวลาอีก ${hours} ชั่วโมง ${minutes} นาที ${seconds} วินาที`;
      } else {
        countdownText.value = 'รีเซ็ตแล้ว';
      }
    };
    // -------------------- Treasure cooldown countdown --------------------
    const updateTreasureCooldown = () => {
      currentTime.value = Date.now();
      if (props.map.treasureAvailableAt && currentTime.value < props.map.treasureAvailableAt.toMillis()) {
        const diff = props.map.treasureAvailableAt.toMillis() - currentTime.value;
        if (diff > 0) {
          const hours = Math.floor(diff / 3600000);
          const minutes = Math.floor((diff % 3600000) / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          treasureCooldownText.value = `${hours} ชั่วโมง ${minutes} นาที ${seconds} วินาที`;
        } else {
          treasureCooldownText.value = '';
        }
      } else {
        treasureCooldownText.value = '';
      }
    };
    // -------------------- React to map change --------------------
    watch(() => props.map, async (newMap) => {
      if (!newMap) return;
      // Reset caches
      zombieDetails.value = {};
      merchantDetails.value = {};
      mutantDetails.value = {};
      villagerDetails.value = {};
      itemDetails.value = {};
      isLoadingZombie.value = {};
      isLoadingMerchant.value = {};
      isLoadingMutant.value = {};
      isLoadingVillager.value = {};
      isLoadingItem.value = {};
      // Reset action UIs
      confirmOpen.value = false;
      confirmResign.value = false;
      showHintOpen.value = false;
      showHintResign.value = false;
      treasureMessage.value = '';
      resignMessage.value = '';
      // Prefetch
      await Promise.all((newMap.zombies || []).map(z => fetchZombieDetails(z.name)));
      await Promise.all((newMap.merchants || []).map(m => fetchMerchantDetails(m.name)));
      await Promise.all((newMap.mutants || []).map(m => fetchMutantDetails(m.name)));
      await Promise.all((newMap.villagers || []).map(v => fetchVillagerDetails(v.name)));
      (newMap.rewards || []).forEach(r => fetchItemDetails(r.itemName));
      if (Array.isArray(newMap.situations)) {
        newMap.situations.forEach(s => (s.rewards || []).forEach(r => fetchItemDetails(r.itemName)));
      }
      if (newMap.isOccupationAllowed && newMap.occupierUid) {
        await fetchOccupierDetails(newMap.occupierUid);
      } else {
        occupierDetails.value = { imageUrl: '', characterName: 'N/A', level: 0, room: 0 };
      }
      // Manage countdowns
      if (newMap.treasureOpened) {
        updateCountdown();
        if (!countdownInterval) {
          countdownInterval = setInterval(updateCountdown, 1000);
        }
      } else {
        if (countdownInterval) {
          clearInterval(countdownInterval);
          countdownInterval = null;
        }
      }
      if (newMap.treasureAvailableAt && currentTime.value < newMap.treasureAvailableAt.toMillis()) {
        updateTreasureCooldown();
        if (!cooldownInterval) {
          cooldownInterval = setInterval(updateTreasureCooldown, 1000);
        }
      } else {
        if (cooldownInterval) {
          clearInterval(cooldownInterval);
          cooldownInterval = null;
        }
      }
    }, { immediate: true });
    // Update current time periodically
    const timeUpdateInterval = setInterval(() => {
      currentTime.value = Date.now();
    }, 1000);
    onUnmounted(() => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (cooldownInterval) clearInterval(cooldownInterval);
      if (timeUpdateInterval) clearInterval(timeUpdateInterval);
    });
    // -------------------- Expose to template --------------------
    return {
      activeTab,
      tabs,
      zombieDetails,
      merchantDetails,
      mutantDetails,
      villagerDetails,
      itemDetails,
      occupierDetails,
      isLoadingZombie,
      isLoadingMerchant,
      isLoadingMutant,
      isLoadingVillager,
      isLoadingItem,
      isLoadingOccupier,
      sortedZombies,
      sortedMerchants,
      sortedMutants,
      sortedVillagers,
      sortedRewards,
      sortedSituations,
      filteredRewards,
      selectedZombie,
      selectedMerchant,
      selectedMutant,
      selectedVillager,
      selectedItem,
      selectedItemType,
      wearableTypes,
      translateZombieType,
      hasNonZeroStats,
      getStars,
      handleImageError,
      showZombieDetails,
      showMerchantDetails,
      showMutantDetails,
      showVillagerDetails,
      showItemDetails,
      openTreasureBox,
      resignOccupier,
      user,
      isCurrentUserOccupier,
      treasureMessage,
      resignMessage,
      countdownText,
      treasureCooldownText,
      isOpeningTreasure,
      isResigning,
      confirmOpen,
      confirmResign,
      showHintOpen,
      showHintResign,
      currentTime,
    };
  },
};
</script>
<style scoped>
/* ===== Custom Scrollbar ===== */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }
/* ===== Animations ===== */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
@keyframes tabSlide { from { width: 0; } to { width: 100%; } }
.animate-tabSlide { animation: tabSlide 0.2s ease-out; }
@keyframes shake {
  0% { transform: translateX(0); }
  10% { transform: translateX(-5px) rotate(-5deg); }
  20% { transform: translateX(5px) rotate(5deg); }
  30% { transform: translateX(-5px) rotate(-5deg); }
  40% { transform: translateX(5px) rotate(5deg); }
  50% { transform: translateX(-5px) rotate(-5deg); }
  60% { transform: translateX(5px) rotate(5deg); }
  70% { transform: translateX(-5px) rotate(-5deg); }
  80% { transform: translateX(5px) rotate(5deg); }
  90% { transform: translateX(-5px) rotate(-5deg); }
  100% { transform: translateX(0); }
}
.animate-shake { animation: shake 2.5s ease-in-out infinite; }
@keyframes spinPulse {
  0% { transform: rotate(0deg); opacity: 0.7; }
  50% { opacity: 1; }
  100% { transform: rotate(360deg); opacity: 0.7; }
}
.animate-spin-pulse {
  animation: spinPulse 1s linear infinite;
  border-right-color: transparent;
  border-bottom-color: transparent;
}
/* ===== Transition for <transition name="fade"> ===== */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
/* ===== Tooltip ===== */
.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 70;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-bottom: 0.5rem;
}
button:hover .tooltip { visibility: visible; opacity: 1; }
/* ===== Mobile ===== */
@media (max-width: 639px) {
  .text-sm { font-size: 0.75rem; }
  .text-base { font-size: 0.875rem; }
  .p-4 { padding: 0.75rem; }
  button[aria-label="ปิดหน้าต่างรายละเอียดไอเทม"],
  button[aria-label="ปิด"] {
    padding: 0.75rem;
    margin: -0.5rem;
    touch-action: manipulation;
  }
  button[aria-label="ปิดหน้าต่างรายละเอียดไอเทม"] i,
  button[aria-label="ปิด"] i { font-size: 1.25rem; }
  .z-60 { max-width: 90%; margin: 0 auto; }
}
/* ===== Extra tiny text utility ===== */
.text-xxs { font-size: 0.7rem; line-height: 1rem; }
/* ====== Occupation UI ====== */
.occ-container { background: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 16px; color: #c7d2fe; }
.occ-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.occ-title { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.occ-title i { color: #818cf8; }
.occ-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; padding: 3px 8px; border-radius: 6px; border: 1px solid transparent; }
.occ-badge[data-allowed="true"] { background: rgba(16,185,129,0.15); color: #6ee7b7; border-color: #065f46; }
.occ-badge[data-allowed="false"] { background: rgba(75,85,99,0.5); color: #e5e7eb; border-color: #4b5563; }
.occ-note { background: rgba(17,24,39,0.6); border: 1px solid #374151; border-radius: 8px; padding: 12px; font-size: 12px; color: #c7d2fe; }
.occ-body { display: flex; flex-direction: column; gap: 16px; }
.occ-kpi-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
@media (min-width: 640px) { .occ-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
.occ-kpi-card { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 8px; background: rgba(17,24,39,0.5); border: 1px solid #374151; }
.occ-kpi-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; background: rgba(99,102,241,0.1); border: 1px solid #3730a3; color: #a5b4fc; }
.occ-kpi-icon i { font-size: 14px; }
.occ-kpi-body { flex: 1; min-width: 0; }
.occ-kpi-label { margin: 0; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; color: rgba(165,180,252,0.8); }
.occ-kpi-value { margin: 2px 0 0; font-size: 14px; font-weight: 700; color: #e0e7ff; }
.occ-kpi-sub { font-size: 10px; color: #a5b4fc; margin-left: 4px; }
.occ-progress { margin-top: 6px; height: 6px; border-radius: 6px; background: #374151; overflow: hidden; }
.occ-progress-bar { height: 100%; background: #6366f1; transition: width .25s ease; }
.occ-section { display: flex; flex-direction: column; gap: 8px; }
.occ-subtitle { margin: 0; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.occ-skeleton { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 8px; background: rgba(17,24,39,0.5); border: 1px solid #374151; }
.occ-skel-avatar { width: 48px; height: 48px; border-radius: 9999px; background: #374151; }
.occ-skel-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.occ-skel-line { display: block; height: 10px; border-radius: 6px; background: #374151; }
.occ-shimmer { position: relative; overflow: hidden; }
.occ-shimmer::after { content: ""; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.06), transparent); animation: occ-shimmer 1.2s infinite; }
@keyframes occ-shimmer { 100% { transform: translateX(100%); } }
.occ-empty { display: flex; align-items: center; gap: 12px; justify-content: space-between; padding: 12px; border-radius: 8px; background: rgba(17,24,39,0.5); border: 1px dashed #4b5563; }
.occ-empty-avatar { width: 40px; height: 40px; border-radius: 9999px; background: #374151; border: 1px solid #4b5563; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 14px; }
.occ-empty-title { margin: 0; font-size: 12px; font-weight: 600; color: #c7d2fe; }
.occ-empty-desc { margin: 2px 0 0; font-size: 10px; color: rgba(199,210,254,0.8); }
.occ-occupier { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px; border-radius: 8px; background: rgba(17,24,39,0.5); border: 1px solid #374151; transition: border-color .2s ease; }
.occ-occupier:hover { border-color: #6366f1; }
.occ-occupier-left { display: flex; align-items: center; gap: 12px; }
.occ-avatar { width: 48px; height: 48px; border-radius: 9999px; object-fit: cover; border: 1px solid #374151; background: #111827; }
.occ-occupier-info { color: #c7d2fe; font-size: 12px; }
.occ-occupier-name { margin: 0; font-weight: 700; line-height: 1.2; color: #e0e7ff; }
.occ-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.occ-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; padding: 3px 8px; border-radius: 6px; border: 1px solid #4b5563; background: rgba(31,41,55,0.6); color: #a5b4fc; }
.occ-pill i { font-size: 10px; color: #a5b4fc; }
.occ-occupier-hint { display: none; text-align: right; font-size: 10px; color: rgba(165,180,252,0.8); }
@media (min-width: 640px) { .occ-occupier-hint { display: block; } }
/* ===== Basic Info & Fee ===== */
.bi-card { background: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 16px; color: #c7d2fe; }
.bi-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.bi-title { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.bi-title i { color: #818cf8; }
.bi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (min-width: 640px) { .bi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 768px) { .bi-grid { grid-template-columns: repeat(3, 1fr); } }
.bi-item { display: flex; align-items: center; gap: 8px; background: rgba(17,24,39,0.5); border: 1px solid #374151; border-radius: 8px; padding: 10px; font-size: 12px; color: #e0e7ff; }
.bi-item i { font-size: 12px; color: #a5b4fc; }
.bi-span2 { grid-column: 1 / -1; }
.fee-card { background: #1f2937; border: 1px solid #374151; border-radius: 8px; padding: 16px; color: #c7d2fe; }
.fee-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.fee-title { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; color: #a5b4fc; }
.fee-title i { color: #818cf8; }
.fee-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (min-width: 640px) { .fee-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 768px) { .fee-grid { grid-template-columns: repeat(4, 1fr); } }
@media (min-width: 1024px) { .fee-grid { grid-template-columns: repeat(5, 1fr); } }
.fee-chip { display: flex; align-items: center; justify-content: center; gap: 6px; background: #374151; border: 1px solid #4b5563; border-radius: 8px; padding: 8px 10px; font-size: 12px; color: #e0e7ff; transition: transform .12s ease, background .15s ease, border-color .15s ease; }
.fee-chip:hover { transform: translateY(-1px); background: #3f4758; border-color: #5b6473; }
.fee-chip i { font-size: 12px; }
.fee-coin { color: #fbbf24; }
.fee-gold { color: #f59e0b; }
.fee-diamond { color: #93c5fd; }
.fee-cash { color: #86efac; }
.fee-credit { color: #a5b4fc; }
/* ===== Treasure Box UI Enhancements ===== */
.treasure-container { background: rgba(17,24,39,0.5); border: 1px solid #374151; border-radius: 8px; padding: 12px; }
.treasure-button:disabled { opacity: 0.6; cursor: not-allowed; }
.treasure-button:hover:not(:disabled) { transform: translateY(-1px); }
.treasure-loading { animation: pulse 1.5s infinite; }
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}
</style>
